import { useState, useEffect } from "react";
import CustomSelect from "../../components/common/CustomSelect";
import { apiCall } from "../../Apis/apiCall";

const PlateNumberComponent = ({ field, formData, setFormData }) => {
  const [countries, setCountries] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [selectedCountryId, setSelectedCountryId] = useState(
    formData?.countryId || ""
  );
  const [selectedAuthorityId, setSelectedAuthorityId] = useState(
    formData?.authorityId || ""
  );
  const [fieldValues, setFieldValues] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({
    countries: true,
    authorities: false,
    config: false,
  });
  const [apiError, setApiError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading((prev) => ({ ...prev, countries: true }));
        const response = await apiCall(
          "get",
          "/get-all-countries?want_pagination=0"
        );
        if (response.data.success) {
          setCountries(response.data.data);
        } else {
          setApiError(response.data.message);
        }
      } catch (err) {
        setApiError("Failed to fetch countries");
      } finally {
        setLoading((prev) => ({ ...prev, countries: false }));
      }
    };
    fetchCountries();
  }, []);

  // Fetch authorities on country change
  useEffect(() => {
    if (!selectedCountryId) {
      setAuthorities([]);
      setCurrentConfig(null);
      setSelectedAuthorityId("");
      return;
    }
    const fetchAuthorities = async () => {
      try {
        setLoading((prev) => ({ ...prev, authorities: true }));
        const response = await apiCall(
          "get",
          `/get-plate-authorities-by-country-id/${selectedCountryId}?want_pagination=0`
        );
        if (response.data.success) {
          setAuthorities(response.data.data);
        } else {
          setApiError(response.data.message);
        }
      } catch (err) {
        setApiError("Failed to fetch authorities");
      } finally {
        setLoading((prev) => ({ ...prev, authorities: false }));
      }
    };
    fetchAuthorities();
  }, [selectedCountryId]);

  // Set config on authority change
  useEffect(() => {
    if (!selectedCountryId || !selectedAuthorityId) {
      setCurrentConfig(null);
      return;
    }
    const selectedAuthority = authorities.find(
      (auth) => auth.authority_id === parseInt(selectedAuthorityId)
    );
    if (selectedAuthority) {
      let config = { ...selectedAuthority };
      // Infer fields if null
      if (!config.fields && config.format_pattern) {
        const parts = config.format_pattern.split(" ");
        config.fields = parts.map((part, index) => {
          const isAlpha = /^[A-Z]+$/.test(part);
          const isNumeric = /^\d+$/.test(part);
          return {
            field_id: 100 + index,
            label: isAlpha
              ? `Letters (${part.length})`
              : isNumeric
              ? `Numbers (${part.length})`
              : `Code (${part.length})`,
            type: isAlpha ? "alpha" : isNumeric ? "numeric" : "alnum",
            max_length: part.length,
            options: null,
            created_at: "2025-10-08T18:39:02",
            updated_at: "2025-10-08T18:39:02",
          };
        });
      }
      setCurrentConfig(config);
    } else {
      setCurrentConfig(null);
    }
  }, [selectedCountryId, selectedAuthorityId, authorities]);

  // NEW: Parse existing plate_number and populate fieldValues when config is ready
  useEffect(() => {
    if (!currentConfig || !formData?.plate_number || isInitialized) return;
    
    const existingPlateNumber = formData.plate_number.trim();
    if (!existingPlateNumber) return;

    // Split the plate number by spaces
    const parts = existingPlateNumber.split(/\s+/);
    
    // Populate fieldValues based on the parts
    const newFieldValues = {};
    parts.forEach((part, index) => {
      if (currentConfig.fields[index]) {
        newFieldValues[index] = part;
      }
    });
    
    setFieldValues(newFieldValues);
    setIsInitialized(true);
  }, [currentConfig, formData?.plate_number, isInitialized]);

  // Reset initialization flag when country/authority changes
  useEffect(() => {
    setIsInitialized(false);
  }, [selectedCountryId, selectedAuthorityId]);

  const validateField = (fieldConfig, value) => {
    if (!value) return "This field is required";
    let regex;
    if (fieldConfig.type === "alpha") {
      regex = /^[A-Z]+$/;
      if (fieldConfig.options) {
        const validChars = fieldConfig.options;
        if (!value.split("").every((char) => validChars.includes(char))) {
          return `Each character must be one of: ${validChars.join(", ")}`;
        }
      }
    } else if (fieldConfig.type === "numeric") {
      regex = /^\d+$/;
    } else if (fieldConfig.type === "alnum") {
      regex = /^[A-Z0-9]+$/;
    } else if (fieldConfig.type === "arabic") {
      regex =
        /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]+$/;
    }
    if (regex && !regex.test(value)) {
      return fieldConfig.type === "arabic"
        ? "Only Arabic letters allowed"
        : fieldConfig.type === "alpha"
        ? "Only uppercase letters (A-Z) allowed"
        : fieldConfig.type === "numeric"
        ? "Only numbers (0-9) allowed"
        : "Invalid characters";
    }
    if (value.length !== fieldConfig.max_length) {
      return `Must be exactly ${fieldConfig.max_length} characters`;
    }
    return null;
  };

  const handleFieldChange = (fieldIndex, value) => {
    const fieldConfig = currentConfig?.fields[fieldIndex];
    if (!fieldConfig) return;

    let filteredValue = value;
    if (fieldConfig.type === "alpha" && fieldConfig.options) {
      filteredValue = value
        .toUpperCase()
        .split("")
        .filter((char) => fieldConfig.options.includes(char))
        .join("");
    } else if (fieldConfig.type === "alpha") {
      filteredValue = value.toUpperCase().replace(/[^A-Z]/g, "");
    } else if (fieldConfig.type === "numeric") {
      filteredValue = value.replace(/[^0-9]/g, "");
    } else if (fieldConfig.type === "alnum") {
      filteredValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    } else if (fieldConfig.type === "arabic") {
      filteredValue = value.replace(
        /[^\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g,
        ""
      );
    }

    if (filteredValue.length > fieldConfig.max_length) {
      filteredValue = filteredValue.slice(0, fieldConfig.max_length);
    }

    const newFieldValues = { ...fieldValues, [fieldIndex]: filteredValue };
    setFieldValues(newFieldValues);

    const error = validateField(fieldConfig, filteredValue);
    const newErrors = { ...errors };
    if (error) {
      newErrors[fieldIndex] = error;
    } else {
      delete newErrors[fieldIndex];
    }
    setErrors(newErrors);

    updateFormData(newFieldValues, newErrors);
  };

  const updateFormData = (values, currentErrors) => {
    if (!currentConfig) return;
    const allFieldsComplete = currentConfig.fields.every(
      (_, index) =>
        values[index] &&
        values[index].length === currentConfig.fields[index].max_length
    );
    const hasErrors = Object.keys(currentErrors).length > 0;
    if (allFieldsComplete && !hasErrors) {
      const plateNumber = constructPlateNumber(values);
      setFormData((prev) => ({
        ...prev,
        [field?.name]: plateNumber,
        countryId: selectedCountryId,
        authorityId: selectedAuthorityId,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field?.name]: "",
        countryId: selectedCountryId,
        authorityId: selectedAuthorityId,
      }));
    }
  };

  const constructPlateNumber = (values) => {
    if (!currentConfig) return "";
    const parts = currentConfig.fields
      .map((_, index) => values[index])
      .filter(Boolean);
    const formatParts = currentConfig.format_pattern.split(" ");
    let result = "";
    for (let i = 0; i < parts.length; i++) {
      result += parts[i];
      if (i < parts.length - 1 && formatParts[i + 1]) {
        result += " ";
      }
    }
    return result;
  };

  const generatePlaceholder = (fieldConfig) => {
    if (fieldConfig.type === "alpha" && fieldConfig.options) {
      const option = fieldConfig.options[0] || "A";
      return option.repeat(fieldConfig.max_length);
    }
    if (fieldConfig.type === "alpha") return "A".repeat(fieldConfig.max_length);
    if (fieldConfig.type === "numeric")
      return "1".repeat(fieldConfig.max_length);
    if (fieldConfig.type === "alnum")
      return "A1"
        .repeat(Math.ceil(fieldConfig.max_length / 2))
        .slice(0, fieldConfig.max_length);
    if (fieldConfig.type === "arabic")
      return "أ ب ج".slice(0, fieldConfig.max_length * 2);
    return "A1"
      .repeat(Math.ceil(fieldConfig.max_length / 2))
      .slice(0, fieldConfig.max_length);
  };

  const getInputProps = (fieldConfig) => {
    const baseProps = {};
    if (fieldConfig.type === "arabic") {
      baseProps.lang = "ar";
      baseProps.dir = "rtl";
      baseProps.style = {
        direction: "rtl",
        textAlign: "right",
        fontFamily: "Arial, sans-serif",
      };
    }
    return baseProps;
  };

  const handleCountryChange = (selected) => {
    const value = selected ? selected.value : "";
    setSelectedCountryId(value);
    setSelectedAuthorityId("");
    setFieldValues({});
    setErrors({});
    setCurrentConfig(null);
  };

  const handleAuthorityChange = (selected) => {
    const value = selected ? selected.value : "";
    setSelectedAuthorityId(value);
    setFieldValues({});
    setErrors({});
  };

  const countryOptions = countries.map((country) => ({
    label: country.country_name,
    value: country.country_id,
  }));
  const authorityOptions = authorities.map((authority) => ({
    label: authority.authority_name,
    value: authority.authority_id,
  }));

  if (apiError) return <div>{apiError}</div>;

  return (
    <div className="plate-number-container">
      <style jsx>{`
        .plate-number-container {
          min-height: 200px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 8px;
        }
        .fields-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 10px;
          margin-bottom: 12px;
        }
        .form-group {
          position: relative;
        }
        .form-select,
        .form-control {
          width: 100%;
          padding: 10px 12px;
          border: 2px solid #e1e5e9;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.1s ease;
          min-height: 42px;
        }
        .form-control.small {
          padding: 8px 10px;
          min-height: 38px;
          text-align: center;
          font-weight: 600;
          font-family: "Courier New", monospace;
        }
        .form-control.arabic-input {
          font-family: "Arial Unicode MS", sans-serif;
          text-align: right;
        }
        .form-select:focus,
        .form-control:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
        .form-select:disabled,
        .form-control:disabled {
          cursor: not-allowed;
          border-color: #e5e7eb;
        }
        .is-invalid {
          border-color: #ef4444 !important;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
        }
        .is-valid {
          border-color: #10b981 !important;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
        }
        .invalid-feedback {
          color: #ef4444;
          font-size: 11px;
          margin-top: 4px;
          display: block;
        }
        .format-display {
          background: var(--white);
          padding: 12px;
          border-radius: 8px;
          border-left: 4px solid var(--primary-600);
          margin-bottom: 12px;
        }
        .format-text {
          font-family: "DIN Next Pro", "Arial", sans-serif;
          font-weight: 700;
          color: #374151;
          font-size: 14px;
        }
        .format-description {
          color: #6b7280;
          font-size: 12px;
          margin-top: 4px;
        }
        .success-indicator {
          background: ${currentConfig ? currentConfig.color_hex : "#ffffff"};
          color: ${currentConfig ? currentConfig.text_color_hex : "#000000"};
          padding: 10px 20px;
          border: 4px solid
            ${currentConfig ? currentConfig.border_color_hex : "#000000"};
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 24px;
          font-weight: 900;
          font-family: ${currentConfig && currentConfig.font_name
              ? currentConfig.font_name
              : "DIN Next Pro"},
            "Arial", sans-serif;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
          text-transform: uppercase;
          width: 300px;
          height: 80px;
          position: relative;
          animation: slideIn 0.3s ease-out;
        }
        .success-indicator::before {
          content: ${currentConfig ? `'${currentConfig.emblem}'` : '""'};
          position: absolute;
          top: 5px;
          left: 10px;
          font-size: 12px;
          color: ${currentConfig ? currentConfig.text_color_hex : "#000000"};
          font-weight: normal;
        }
        .success-icon {
          display: none;
        }
        .label {
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
          font-size: 13px;
          display: block;
        }
        .field-label {
          font-weight: 500;
          color: #4b5563;
          margin-bottom: 4px;
          font-size: 12px;
          display: block;
          text-align: center;
        }
        .react-select__menu {
          transition: opacity 0.1s ease, transform 0.1s ease;
          will-change: opacity, transform;
          z-index: 10000 !important;
          background: var(--input-bg, #fff);
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }
        .react-select__menu-list {
          max-height: 150px;
          scrollbar-width: thin;
          scrollbar-color: #4f46e5 #e5e7eb;
        }
        .react-select__option {
          transition: background-color 0.08s ease, color 0.08s ease;
          cursor: pointer !important;
          padding: 8px 12px;
        }
        .react-select__option:hover,
        .react-select__option--is-focused {
          background-color: var(--primary-600, #4f46e5) !important;
          color: var(--white, #fff) !important;
        }
        .react-select__option--is-selected {
          background-color: var(--primary-600, #4f46e5) !important;
          color: var(--white, #fff) !important;
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (max-width: 768px) {
          .form-row {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          .fields-row {
            grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
            gap: 8px;
          }
          .plate-number-container {
            min-height: 250px;
          }
          .success-indicator {
            width: 100%;
            font-size: 18px;
            height: 60px;
            padding: 8px 16px;
          }
          .success-indicator::before {
            font-size: 10px;
          }
          .react-select__menu-list {
            max-height: 120px;
          }
        }
      `}</style>

      <div className="form-row">
        <div className="form-group">
          <CustomSelect
            name="country"
            value={
              countryOptions.find((opt) => opt.value === selectedCountryId) ||
              null
            }
            onChange={handleCountryChange}
            options={countryOptions}
            placeholder={loading.countries ? "Loading..." : "Select Country"}
            isSearchable={true}
            isClearable={true}
            isMulti={false}
            labelKey="label"
            valueKey="value"
            required={field?.required}
            isLoading={loading.countries}
            menuPortalTarget={document.body}
            onMenuOpen={() => {
              document.body.style.cursor = "default";
            }}
            onMenuClose={() => {
              document.body.style.cursor = "default";
            }}
          />
        </div>
        <div className="form-group">
          <CustomSelect
            name="authority"
            value={
              authorityOptions.find(
                (opt) => opt.value === selectedAuthorityId
              ) || null
            }
            onChange={handleAuthorityChange}
            options={authorityOptions}
            placeholder={
              selectedCountryId
                ? loading.authorities
                  ? "Loading..."
                  : "Select Authority"
                : "Select Country First"
            }
            isSearchable={true}
            isClearable={true}
            isMulti={false}
            labelKey="label"
            valueKey="value"
            isDisabled={!selectedCountryId || loading.authorities}
            required={field?.required}
            isLoading={loading.authorities}
            menuPortalTarget={document.body}
            onMenuOpen={() => {
              document.body.style.cursor = "default";
            }}
            onMenuClose={() => {
              document.body.style.cursor = "default";
            }}
          />
        </div>
      </div>

      {currentConfig && !loading.config && (
        <div className="format-display">
          <div className="format-text">
            Format: {currentConfig.format_pattern}
          </div>
          <div className="format-description">
            Enter each part separately in the fields below
          </div>
        </div>
      )}

      {currentConfig && !loading.config && (
        <div className="fields-row">
          {currentConfig.fields.map((fieldConfig, index) => (
            <div key={index} className="form-group">
              <label className="field-label">{fieldConfig.label}</label>
              <input
                type="text"
                className={`form-control small ${
                  errors[index]
                    ? "is-invalid"
                    : fieldValues[index]?.length === fieldConfig.max_length
                    ? "is-valid"
                    : ""
                } ${fieldConfig.type === "arabic" ? "arabic-input" : ""}`}
                placeholder={generatePlaceholder(fieldConfig)}
                value={fieldValues[index] || ""}
                onChange={(e) => handleFieldChange(index, e.target.value)}
                maxLength={fieldConfig.max_length}
                required={field?.required}
                {...getInputProps(fieldConfig)}
              />
              {errors[index] && (
                <div className="invalid-feedback">{errors[index]}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {formData[field?.name] && Object.keys(errors).length === 0 && (
        <div
          className="success-indicator"
          style={{
            background: currentConfig ? currentConfig.color_hex : "#ffffff",
            color: currentConfig ? currentConfig.text_color_hex : "#000000",
            border: `4px solid ${
              currentConfig ? currentConfig.border_color_hex : "#000000"
            }`,
            fontFamily:
              currentConfig && currentConfig.font_name
                ? `${currentConfig.font_name}, Arial, sans-serif`
                : "DIN Next Pro, Arial, sans-serif",
          }}
        >
          <span>{formData[field?.name]}</span>
        </div>
      )}
    </div>
  );
};

export default PlateNumberComponent;